import React, { useState } from "react"
import { Plus, GitBranch } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog"
import { useToast } from "src/components/ui/toast"
import { usePipelines, useCreatePipeline } from "src/features/pipelines/hooks/usePipelines"
import { PipelineCard } from "src/features/pipelines/components/PipelineCard"
import { Can } from "src/components/shared/Can"

export default function PipelinesConfigPage() {
  const { toast } = useToast()
  const [filter, setFilter] = useState("all")
  const { pipelines, loading, reload } = usePipelines(filter === "all" ? undefined : filter)

  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState("")
  const [newEntity, setNewEntity] = useState("deal")
  const { mutate: doCreate, isPending: isCreating } = useCreatePipeline()

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!newName.trim()) return
    try {
      await doCreate({ name: newName.trim(), entity_type: newEntity })
      toast({ title: "Pipeline created" })
      setShowCreate(false)
      setNewName("")
      setNewEntity("deal")
      reload()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
    }
  }

  const dealPipelines = pipelines.filter((p) => p.entity_type === "deal")
  const leadPipelines = pipelines.filter((p) => p.entity_type === "lead")

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">Pipelines</h2>
          <p className="text-[13px] text-slate-500">
            Configure the stages your leads and deals move through.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="deal">Deals</SelectItem>
              <SelectItem value="lead">Leads</SelectItem>
            </SelectContent>
          </Select>
          <Can resource="settings" action="update">
            <Button size="sm" className="gap-1.5" onClick={() => setShowCreate(true)}>
              <Plus className="h-3.5 w-3.5" /> New Pipeline
            </Button>
          </Can>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      )}

      {!loading && pipelines.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-16 text-center">
          <GitBranch className="mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm font-medium text-slate-500">No pipelines yet</p>
          <p className="mt-1 text-xs text-slate-400">Create your first pipeline to get started.</p>
        </div>
      )}

      {/* Deal pipelines */}
      {(filter === "all" || filter === "deal") && dealPipelines.length > 0 && (
        <div className="space-y-3">
          {filter === "all" && (
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Deal Pipelines</h3>
          )}
          {dealPipelines.map((p) => (
            <PipelineCard key={p.id} pipeline={p} onRefresh={reload} />
          ))}
        </div>
      )}

      {/* Lead pipelines */}
      {(filter === "all" || filter === "lead") && leadPipelines.length > 0 && (
        <div className="space-y-3">
          {filter === "all" && (
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Lead Pipelines</h3>
          )}
          {leadPipelines.map((p) => (
            <PipelineCard key={p.id} pipeline={p} onRefresh={reload} />
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Pipeline</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Enterprise Sales" autoFocus />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Type</label>
              <Select value={newEntity} onValueChange={setNewEntity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deal">Deal Pipeline</SelectItem>
                  <SelectItem value="lead">Lead Pipeline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={isCreating || !newName.trim()}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
